import { IonCol, IonIcon, IonLabel, IonRow } from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';
import './serato.scss';
import { cubeOutline } from 'ionicons/icons';
import { on, removeListener, run } from '../../../../helper/electron';
import TreeView, { flattenTree, INode } from 'react-accessible-treeview';
import { IFlatMetadata } from 'react-accessible-treeview/dist/TreeView/utils';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { GridReadyEvent } from 'ag-grid-community';
import { iTrack } from '../../../../types/ITrack';

type SeratoCrate = {
  path: string[];
  file: string;
  last_modified_at: string;
};

interface ImportCrateProps {
  setCrateDescription: (description: string) => void;
  setTrackList: (trackList: iTrack[]) => void;
  setSuggestedCrateName?: (name: string) => void;
}

type TrackType = {
  filepath: string;
  artist: string[];
  title: string;
  bpm: number;
  key: string;
};

const ImportCrates: React.FC<ImportCrateProps> = ({
  setCrateDescription,
  setTrackList,
  setSuggestedCrateName,
}) => {
  const theme = useSelector((state: RootState) => state.themeState.theme);
  const [crates, setCrates] = useState<SeratoCrate[]>([]);
  const [crateTree, setCrateTree] = useState<ReturnType<typeof flattenTree>>();
  const [selectedCrateIds, setSelectedCrateIds] = useState<number[]>([]);
  const [selectedCrates, setSelectedCrates] = useState<INode<IFlatMetadata>[]>(
    [],
  );
  const [flattedTree, setFlattedTree] = useState<INode<IFlatMetadata>[]>();
  const [tracks, setTracks] = useState<TrackType[]>([]);
  const [gridApi, setGridApi] = useState<any>();
  const [clickedCrateIds, setClickedCrateIds] = useState<number[]>([]);
  const [treeSelectedIds, setTreeSelectedIds] = useState<string[]>([]);
  const [treeKey, setTreeKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const buildTree = (seratoCrates: SeratoCrate[]) => {
    if (!seratoCrates || seratoCrates.length === 0) {
      const emptyTree = { name: 'Crates', children: [] };
      const flattened = flattenTree(emptyTree);
      setFlattedTree(flattened);
      return;
    }
    type TreeNode = {
      name: string;
      children?: TreeNode[];
      metadata?: IFlatMetadata;
    };

    const tree: TreeNode = { name: 'Crates', children: [] };

    for (const seratoCrate of seratoCrates) {
      let currentNode = tree;

      for (const part of seratoCrate.path) {
        let childNode =
          currentNode.children &&
          currentNode.children.find((node) => node.name === part);

        if (!childNode) {
          childNode = { name: part };
          if (!currentNode.children) currentNode.children = [];
          childNode.metadata = { file: seratoCrate.file };
          currentNode.children.push(childNode);
        }

        currentNode = childNode;
      }
    }

    const flattened = flattenTree(tree);
    setFlattedTree(flattened);
  };

  const getSeratoCrateTracks = useCallback(
    (filename: string): Promise<any[]> => {
      return new Promise((resolve) => {
        let isResolved = false;

        const handleData = (data: any) => {
          if (isResolved) return;
          isResolved = true;

          removeListener('serato/get-crate-tracks', handleData);
          clearTimeout(timeout);

          if (!data || !Array.isArray(data)) {
            console.warn(`Invalid or no data received for ${filename}`);
            resolve([]);
            return;
          }

          resolve(data);
        };

        const timeout = setTimeout(() => {
          if (isResolved) return;
          isResolved = true;

          removeListener('serato/get-crate-tracks', handleData);
          console.warn(`Timeout loading crate tracks for ${filename}`);
          resolve([]);
        }, 10000);

        on('serato/get-crate-tracks', handleData);
        run('serato/get-crate-tracks', filename);
      });
    },
    [],
  );

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  const [columnDefs] = useState([
    { field: 'id', hide: true },
    {
      field: 'artist',
      headerName: 'Artist',
      flex: 1,
      resizable: true,
      filter: false,
      sortable: true,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      resizable: true,
      filter: false,
      sortable: true,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
    },
    {
      field: 'bpm',
      headerName: 'BPM',
      width: 70,
      resizable: true,
      filter: false,
      sortable: true,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
    },
    {
      field: 'key',
      headerName: 'Key',
      width: 70,
      resizable: true,
      filter: false,
      sortable: true,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
    },
  ]);

  useEffect(() => {
    on(`serato/get-crates`, (data) => {
      setCrates(data);
    });
    run('serato/get-crates');
    return () => {
      removeListener('serato/get-crates');
    };
  }, []);

  useEffect(() => {
    buildTree(crates);
    setTreeSelectedIds([]);
    setSelectedCrateIds([]);
    setClickedCrateIds([]);
  }, [crates]);

  useEffect(() => {
    setCrateTree(flattedTree);
    setTreeSelectedIds([]);
    setSelectedCrateIds([]);
    setClickedCrateIds([]);
    setTreeKey((prev) => prev + 1);
  }, [flattedTree]);

  useEffect(() => {
    if (!flattedTree || clickedCrateIds.length === 0) return;
    const selectedCratesPopulated = flattedTree.filter((item) =>
      clickedCrateIds.includes(Number(item.id)),
    );
    setSelectedCrates(selectedCratesPopulated);

    const fetchTracks = async () => {
      setIsLoading(true);
      let allTracks: TrackType[] = [];
      try {
        await Promise.all(
          selectedCratesPopulated.map(async (item) => {
            if (item.metadata?.file) {
              const tracks = await getSeratoCrateTracks(
                String(item.metadata.file),
              );
              allTracks = allTracks.concat(tracks || []);
            }
          }),
        );

        const uniqueTracks = allTracks
          .filter(
            (track, index, self) =>
              track &&
              index === self.findIndex((t) => t.filepath === track.filepath),
          )
          .map((track) => ({
            ...track,
            bpm: track.bpm ? Math.round(track.bpm) : 0,
          }));

        setTracks(uniqueTracks);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTracks();
  }, [clickedCrateIds, flattedTree]);

  useEffect(() => {
    const trackList = tracks.map((data, index) => {
      return {
        ID: '', // This is placeholder, these tracks don't have an id
        Artist: Array.isArray(data.artist)
          ? data.artist.join(', ')
          : data.artist || '',
        Title: data.title ? data.title : '',
        Preview: data.filepath ? data.filepath : '',
        // TODO: Try to get down to one of these definitions
        // PreviewURL: data.filepath ? data.filepath : '',
        duration: 0,
        BPM: data.bpm ? data.bpm : 0,
        energy: 0,
        danceability: 0,
        valence: 0,
        Key: data.key ? data.key : '',
        // TODO: Probably transform key on api side
        // ArtistID: '',
        // AlbumCover: '',
        // PreviewURL: '',
        // SpotifyID: '',
      };
    });
    setTrackList(trackList);

    const crateExportNameString =
      Array.isArray(selectedCrates) && selectedCrates.length
        ? selectedCrates.map((crate) => crate.name).join(', ')
        : '';

    setCrateDescription(crateExportNameString);

    // Auto-fill crate name with the selected crate name(s)
    if (crateExportNameString) {
      setSuggestedCrateName?.(crateExportNameString);
    }
  }, [tracks, selectedCrates, setSuggestedCrateName]);

  const validTreeSelectedIds = crateTree
    ? treeSelectedIds.filter((id) =>
        crateTree.some((node) => String(node.id) === id),
      )
    : [];

  return (
    <>
      <div className="serato-crates-ctn">
        {crateTree && crateTree.length > 0 && (
          <IonRow>
            <IonCol size="12" sizeMd="6">
              <TreeView
                key={treeKey}
                data={crateTree}
                aria-label="crate tree"
                selectedIds={[]}
                togglableSelect={false}
                clickAction="EXCLUSIVE_SELECT"
                multiSelect={false}
                nodeRenderer={({ element, getNodeProps, level }) => {
                  const id = String(element.id);
                  const { onClick, ...otherProps } = getNodeProps();
                  return (
                    <div
                      {...otherProps}
                      onClick={(e) => {
                        if (onClick) onClick(e);
                        setTreeSelectedIds([id]);
                        setSelectedCrateIds([Number(id)]);
                        setClickedCrateIds([Number(id)]);
                      }}
                      style={{
                        paddingLeft: 20 * level,
                        cursor: 'pointer',
                        background: treeSelectedIds.includes(id)
                          ? '#ca691d'
                          : 'transparent',
                      }}
                    >
                      <IonIcon icon={cubeOutline} />
                      <IonLabel>{element.name}</IonLabel>
                    </div>
                  );
                }}
              />
              {isLoading && (
                <div style={{ padding: '10px', textAlign: 'center' }}>
                  Loading tracks...
                </div>
              )}
            </IonCol>
            <IonCol size="12" sizeMd="6">
              <div
                className="ag-theme-alpine"
                style={{ height: 400, width: '100%' }}
              >
                <AgGridReact
                  className={`ag-theme-alpine${
                    theme === 'dark' ? '-dark' : ''
                  }`}
                  rowData={tracks}
                  columnDefs={columnDefs}
                  onGridReady={onGridReady}
                  loading={isLoading}
                />
              </div>
            </IonCol>
          </IonRow>
        )}
      </div>
    </>
  );
};

export default ImportCrates;
